import dayjs from 'dayjs'
import { useAuth } from '../context/AuthenticationContext'
import { useEffect, useState } from 'react'
import utc from 'dayjs/plugin/utc'

export const datePickerConverter = (dateString) => {
    if (dateString) {
        if (dateString?.$d) {
            return dayjs(dateString?.$d?.toString()?.substring(0, 10))
        } else {
            return dayjs(dateString?.substring(0, 10))
        }
    } else {
        return dayjs('L')
    }
}

export const dateValidator = (value: any) => {
    const parsed = dayjs(value)

    return parsed.isValid() ? parsed : null
}

// Calibration production dates are calendar dates, stored as UTC midnight of
// that day ("2023-09-18T00:00:00.000Z"). Reading the stored value as a local
// time showed the previous day in US time zones (Sep 17, 20:00 EDT), and
// re-saving it could move the date. These two helpers keep the day fixed in
// every time zone: read the YYYY-MM-DD part for display, write that day back
// as UTC midnight.
export const calendarDateFromStored = (value: any) => {
    if (!value) return null
    const iso =
        typeof value === 'string' ? value : dayjs(value).toISOString?.() || ''
    const parsed = dayjs(iso.substring(0, 10))

    return parsed.isValid() ? parsed : null
}

export const calendarDateToStored = (value: any) => {
    const parsed = dayjs(value)

    return parsed.isValid()
        ? `${parsed.format('YYYY-MM-DD')}T00:00:00.000Z`
        : null
}

// ECM calibration checksums are 32-bit values, i.e. always 8 hex digits.
// PCM's tracking tools print them as "$" + hex and drop leading zeros
// ("$813EE0A" is 0813EE0A), so typed or pasted values are cleaned here before
// they are validated: whitespace, a leading "$" or "0x" are removed and the
// letters upper-cased.
export const cleanChecksumInput = (value: any) =>
    String(value ?? '')
        .replace(/\s+/g, '')
        .replace(/^\$/, '')
        .replace(/^0x/i, '')
        .toUpperCase()

// The checksum to store, or null when the input cannot be one. The long form
// accepts 7 hex digits and restores the one dropped leading zero; the short
// form must be exactly 4 hex digits.
export const normalizeChecksum = (value: any, short = false) => {
    const hex = cleanChecksumInput(value)

    if (!/^[0-9A-F]+$/.test(hex)) return null
    if (short) return hex.length === 4 ? hex : null
    if (hex.length === 8) return hex
    if (hex.length === 7) return `0${hex}`

    return null
}

export const parseFormWithFile = (data: any, files: any) => {
    // vanilla forms with just files
    const body = new FormData()

    body.append('data', JSON.stringify(data))

    files.forEach((file) => body.append('file', file))

    console.log(body.getAll('file'), 'Files')

    return body
}

export const parseFormWithFileStringified = (data: any, files: any) => {
    // stringified version
    const body = new FormData()
    Object.keys(data).forEach((key) => {
        const value = data[key]
        body.append(
            key,
            typeof value === 'object' ? JSON.stringify(value) : value
        )
    })

    files.forEach((file) => body.append('file', file))

    console.log(body.getAll('file'), 'Files')

    return body
}

export const ParseSingularFile = (files: any) => {
    // singular files without form data
    const body = new FormData()

    files.forEach((file) => body.append('file', file))

    console.log(body.getAll('file'), 'Files')

    return body
}

export const parseFormWithIndentifier = (data: any, files: any) => {
    // forms that have multiple types of the same file type
    const body = new FormData()

    Object.keys(data).forEach((key) => body.append(`${key}`, data[key]))

    files.forEach((file) => {
        if (file.name) {
            const newFileName = file.tag
                ? `${file.tag}-${file.name}`
                : file.name

            const newFile = new File([file], newFileName, {
                type: file.type,
                lastModified: file.lastModified,
            })

            body.append('file', newFile)
        }
    })

    console.log(body.getAll('file'), 'Files')
    return body
}

export const parseFormWithIndentifierBoatModels = (data: any, files: any) => {
    const body = new FormData()

    Object.keys(data).forEach((key) => {
        const value = data[key]

        // Special handling for ownersResources array
        if (key === 'ownersResources' && Array.isArray(value)) {
            value.forEach((resource, index) => {
                if (resource.description) {
                    body.append(
                        `ownersResources[${index}][description]`,
                        resource.description
                    )
                }
                if (resource.resourceType) {
                    body.append(
                        `ownersResources[${index}][resourceType]`,
                        resource.resourceType
                    )
                }
                if (resource.resource) {
                    if (typeof resource.resource === 'string') {
                        body.append(
                            `ownersResources[${index}][resource]`,
                            resource.resource
                        )
                    }
                    if (resource.icon) {
                        if (typeof resource.icon === 'string') {
                            body.append(
                                `ownersResources[${index}][icon]`,
                                resource.icon
                            )
                        }
                    }
                    if (resource.title) {
                        body.append(
                            `ownersResources[${index}][title]`,
                            resource.title
                        )
                    }

                    // If it's a File object, append it as a file
                    else if (resource.resource instanceof File) {
                        const file = resource.resource
                        const newFileName = resource.resourceType
                            ? `${resource.resourceType}-${file.name}`
                            : file.name

                        const renamedFile = new File([file], newFileName, {
                            type: file.type,
                            lastModified: file.lastModified,
                        })

                        body.append(
                            `ownersResources[${index}][file]`,
                            renamedFile
                        )
                    }
                }
            })
        } else {
            body.append(
                key,
                typeof value === 'object' ? JSON.stringify(value) : value
            )
        }
    })

    files.forEach((file) => {
        if (file.name) {
            const newFileName = file.tag
                ? `${file.tag}-${file.name}`
                : file.name

            const newFile = new File([file], newFileName, {
                type: file.type,
                lastModified: file.lastModified,
            })

            // Check if the file tag is like 'ownersResources[0]'
            if (file.tag && file.tag.startsWith('ownersResources[')) {
                body.append(`${file.tag}[file]`, newFile)
            } else {
                body.append('file', newFile) // for global files like modelManual, etc.
            }
        }
    })

    console.log([...body.entries()], 'FormData entries')
    return body
}

export const excelFileParser = (data: any, file: File | null) => {
    const body = new FormData()

    // Append all form data key-value pairs to FormData
    Object.keys(data).forEach((key) => {
        body.append(key, data[key])
    })

    // Append the file (if present)
    if (file) {
        body.append('file', file)
    }

    console.log(body.getAll('file'), 'Files')
    return body
}

export const convertShortDate = (date: Date) => {
    const convertedDateString = `${date.getMonth()}/${date.getDate()}/${date.getFullYear()} ${date.getHours()}:${
        date.getMinutes().toString().length === 1
            ? `0${date.getMinutes()}`
            : `${date.getMinutes()}`
    }`
    return convertedDateString
}

export const basicValidator = ({ target }, formValidation, setState) => {
    const { name, value } = target

    if (name && formValidation && setState) {
        if (value !== '' || value !== null) {
            return setState({
                formValidation: {
                    ...formValidation,
                    [`${name}Validation`]: false,
                    isFormDirty: true,
                },
            })
        } else {
            return setState({
                formValidation: {
                    ...formValidation,
                    [`${name}Validation`]: true,
                    isFormDirty: true,
                },
            })
        }
    }
}

export const standardColumnMapper = (defaultColumns: any, data: any[]) => {
    if (data && data.length !== 0) {
        const sanitizedDataArray = data.map((item) => {
            const sanitizedData: any = {}
            defaultColumns.forEach((column) => {
                let value: any = item
                const keys = column.split('.')

                keys.forEach((key) => {
                    if (value && typeof value === 'object' && key in value) {
                        value = value[key]
                    } else {
                        value = undefined
                        return
                    }
                })
                let columnName = keys[keys.length - 1]

                sanitizedData[columnName] = value
            })
            return { ...sanitizedData, id: item._id, ...item }
        })

        return sanitizedDataArray
    }
    return []
}

export const columnMapper = (defaultColumns: string[], data: any[]) => {
    if (data && data.length !== 0) {
        const sanitizedDataArray = data.map((item, index) => {
            const sanitizedData: any = {}
            defaultColumns.forEach((column) => {
                let value: any = item
                const keys = column.split('.')
                let columnPath = ''
                let lastKey = ''
                keys.forEach((key) => {
                    if (value && typeof value === 'object' && key in value) {
                        value = value[key]
                        columnPath += columnPath ? `.${key}` : key
                        lastKey = key
                    } else {
                        return value
                    }
                })

                let columnName = keys[keys.length - 1]

                lastKey = lastKey.replace(/_/g, '')

                if (
                    sanitizedData[columnName] !== undefined ||
                    columnName.includes('_')
                ) {
                    columnName = `${keys[0]}${lastKey}`
                }

                sanitizedData[columnName] = value
            })
            return { ...sanitizedData, id: item._id, ...item }
        })

        return sanitizedDataArray
    }
    return []
}

// export const permissionsCheckOriginal = (userPermissions: any, component: string) => {
//     const { userRole } = userPermissions
//     const currentUser = useAuth()
//     const auth = currentUser.auth

//     if (userRole !== 'Super Admin') {
//         if (userRole !== 'Admin') {
//             if (
//                 userPermissions[component] &&
//                 userPermissions[component].write &&
//                 userPermissions[component].write === true
//             ) {
//                 return false
//             } else {
//                 return true
//             }
//         } else {
//             return false
//         }
//     } else {
//         return false
//     }
// }

export const permissionsCheck = (
    userProfilePermissions: any,
    component: string
) => {
    const currentUser = useAuth()
    const auth = currentUser.auth
    const { userProfile } = auth
    const { superAdmin } = userProfile

    if ((superAdmin && superAdmin !== true) || superAdmin === undefined) {
        if (
            userProfilePermissions[component] &&
            userProfilePermissions[component].write &&
            userProfilePermissions[component].write === true
        ) {
            return false
        } else {
            return true
        }
    } else {
        return false
    }
}

// PURE helper — must NOT call hooks. It is invoked inside .map/.some loops in
// the nav drawer, so a hook here (useAuth) would run a data-dependent number of
// times and crash React ("rendered fewer hooks than expected") whenever the
// filtered nav children change shape between renders (i.e. for any non-super
// admin). `superAdmin` is passed in by the caller, which already has it.
export const navPermissionsCheck = (
    userProfilePermissions: any,
    component: any,
    superAdmin?: boolean
) => {
    if (superAdmin === true) {
        return false
    }
    return userProfilePermissions?.[component]?.read === true ? false : true
}
interface handleActivateProps {
    activateData: { ids: Set<string> }
    data: []
    returnKey: string
    viewDeactivated: boolean
    setState: (e: any) => void
    getData: (e: boolean) => void
    route: any
    setToastMessage: any
    page: string
}
export const handleActivation = async ({
    activateData,
    data,
    returnKey,
    viewDeactivated,
    setState,
    getData,
    route,
    setToastMessage,
    page,
}: handleActivateProps) => {
    try {
        let res: any

        if (activateData?.ids?.size !== 0) {
            const findDataToActivate = data
                .map((item: any) => {
                    if (activateData?.ids?.has(item?._id)) {
                        return {
                            _id: item?._id,
                            [returnKey]: item?.[returnKey],
                        }
                    }
                })
                .filter((item) => item !== undefined)

            if (!viewDeactivated) {
                res = await route({
                    [page]: findDataToActivate,
                    activate: false,
                })
            } else {
                res = await route({
                    [page]: findDataToActivate,
                    activate: true,
                })
            }

            if (res?.response?.status === 400) {
                setToastMessage(res.response.data, 'error')
            }

            if (res && res.data && res.status === 200) {
                setToastMessage(
                    `${
                        !viewDeactivated ? 'Deactivated' : 'Activated'
                    } ${page}: ${res.data.map((item: string) => ` ${item}`)}`
                )
                setState({ activateData: [] })
                return getData(!viewDeactivated)
            }
        } else {
            setToastMessage(
                `No ${page} selected to ${
                    !viewDeactivated ? 'deactivate' : 'activate'
                }`,
                'error'
            )
        }
    } catch (error: any) {
        console.error('Error deactivating data:', error)
        setToastMessage(error?.message, 'error')
    }
}

interface handleDeletionProps {
    activateData: { ids: Set<string> }
    data: []
    returnKey: string
    viewDeactivated: boolean
    setState: (e: any) => void
    getData: (e: boolean) => void
    route: any
    setToastMessage: any
    page: string
}
export const handleDeletion = async ({
    activateData,
    data,
    returnKey,
    viewDeactivated,
    setState,
    getData,
    route,
    setToastMessage,
    page,
}: handleDeletionProps) => {
    try {
        let res: any

        if (activateData?.ids?.size !== 0) {
            const findDataToActivate = data
                .map((item: any) => {
                    if (activateData?.ids?.has(item?._id)) {
                        return {
                            _id: item?._id,
                            [returnKey]: item?.[returnKey],
                        }
                    }
                })
                .filter((item) => item !== undefined)

            if (!viewDeactivated) {
                res = await route({
                    [page]: findDataToActivate,
                    activate: false,
                })
            } else {
                res = await route({
                    [page]: findDataToActivate,
                    activate: true,
                })
            }

            if (res && res.data && res.status === 200) {
                setToastMessage(
                    `Deleted ${page}: ${res.data.map(
                        (item: string) => ` ${item}`
                    )}`
                )
                setState({ activateData: [] })
                return getData(!viewDeactivated)
            }
        } else {
            setToastMessage(`No ${page} selected to Delete`, 'error')
        }
    } catch (error: any) {
        console.error('Error deactivating data:', error)
        setToastMessage(error?.message, 'error')
    }
}

interface handleCloneProps {
    cloneData: { ids: Set<string> }
    data: []
    returnKey: string
    viewDeactivated: boolean
    setState: (e: any) => void
    getData: (e: boolean) => void
    route: any
    setToastMessage: any
    page: string
}
export const handleClone = async ({
    cloneData,
    data,
    returnKey,
    viewDeactivated,
    setState,
    getData,
    route,
    setToastMessage,
    page,
}: handleCloneProps) => {
    try {
        let res: any
        if (cloneData?.ids?.size !== 0) {
            const findDataToActivate = data
                .map((item: any) => {
                    if (cloneData?.ids?.has(item?._id)) {
                        const clonedItem = {
                            [returnKey]: item?.[returnKey],
                            ...item,
                        }
                        delete clonedItem._id
                        return clonedItem
                    }
                })
                .filter((item) => item !== undefined)

            if (!viewDeactivated) {
                res = await route({
                    [page]: findDataToActivate,
                    activate: false,
                })
            } else {
                res = await route({
                    [page]: findDataToActivate,
                    activate: true,
                })
            }

            if (res && res.data && res.status === 200) {
                setToastMessage(
                    `Cloned ${page}: ${res.data.map(
                        (item: string) => ` ${item}`
                    )}`
                )
                setState({ activateData: [] })
                return getData(!viewDeactivated)
            }
        } else {
            setToastMessage(`No ${page} selected to clone.`, 'error')
        }
    } catch (error: any) {
        console.error('Error cloning data:', error)
        setToastMessage(error?.message, 'error')
    }
}

export const parseTelemetryData = (telemetryData: any) => {
    let parsedTelemetryData: any = []
    if (telemetryData && telemetryData.length !== 0) {
        parsedTelemetryData = telemetryData.map((dataSet: any) => {
            const parsedJSON = JSON.parse(dataSet.data)
            if (Object.keys(parsedJSON).length !== 0) {
                return {
                    ...dataSet,
                    data: parsedJSON,
                }
            }
        })
    }
    return parsedTelemetryData
}

export const parseTelemetryDataColumns = (telemetryData) => {
    let defaultColumns: any = []
    if (telemetryData && telemetryData?.length !== 0) {
        telemetryData.map((dataSet) => {
            const parsedJSON = JSON.parse(dataSet.data)
            if (Object.keys(parsedJSON).length !== 0) {
                defaultColumns.push(...Object.keys(parsedJSON))
                return defaultColumns
            }
        })
        defaultColumns = new Set(defaultColumns.map((column) => column))
        defaultColumns = ['fullHin', 'timestamp', ...defaultColumns]
    }

    return defaultColumns
}

export const parseTelemetryDataGrid = (telemetryData: any) => {
    let parsedTelemetryData: any = []
    if (telemetryData && telemetryData.length !== 0) {
        parsedTelemetryData = telemetryData.map((dataSet: any) => {
            const parsedJSON = JSON.parse(dataSet.data)
            if (
                Object.keys(parsedJSON).length !== 0 &&
                Object.keys(dataSet.boat).length
            ) {
                return {
                    timestamp: dataSet?.timestamp,
                    id: `${dataSet?.boat?._id}-${Math.random()}`,
                    fullHin: dataSet?.boat?.fullHin,
                    manufacturer: dataSet?.boat?.manufacturer?.manufacturerName,
                    ...parsedJSON,
                }
            }
        })
    }
    return parsedTelemetryData
}

export function useDebounce<T>(value: T, delay: number = 500): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value)
        }, delay)

        return () => {
            clearTimeout(handler)
        }
    }, [value, delay])

    return debouncedValue
}

dayjs.extend(utc)

export const universalDateConverter = (dateString: string) => {
    return dayjs.utc(dateString).local().format('MMM D, YYYY h:mm A')
}
